import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { X, Star, Zap, Gem, Crown, Check, Gift } from "lucide-react";

interface SubscriptionBenefitsProps {
  isOpen: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    backgroundColor: "#1a1a1a",
    color: "white",
    borderRadius: "16px",
    maxWidth: "800px",
    [theme.breakpoints.down("sm")]: {
      margin: "16px",
      maxWidth: "none",
    },
  },
  dialogTitle: {
    borderBottom: "1px solid #333",
    padding: "20px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& h2": {
      fontSize: "24px",
      fontWeight: 600,
      color: "#FE5A97",
      margin: 0,
    },
  },
  closeButton: {
    color: "#FE5A97",
    minWidth: "auto",
    padding: "8px",
  },
  dialogContent: {
    padding: "24px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },
  benefitsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
    marginTop: "20px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: "16px",
    },
  },
  benefitsTier: {
    background: "#2a2a2a",
    borderRadius: "12px",
    padding: "20px",
    border: "2px solid transparent",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
    },
  },
  tierFreemium: {
    borderColor: "#28a745",
    "&:hover": {
      borderColor: "#20c997",
    },
  },
  tierOnyx: {
    borderColor: "#6c757d",
    "&:hover": {
      borderColor: "#8a9199",
    },
  },
  tierSapphire: {
    borderColor: "#17a2b8",
    "&:hover": {
      borderColor: "#20c997",
    },
  },
  tierDiamond: {
    borderColor: "#ffc107",
    "&:hover": {
      borderColor: "#ffd43b",
    },
  },
  tierPlatinum: {
    borderColor: "#667eea",
    background: "linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)",
    "&:hover": {
      borderColor: "#8b9cf7",
    },
  },
  tierHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },
  tierIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "rgba(254, 90, 151, 0.1)",
  },
  tierName: {
    fontSize: "18px",
    fontWeight: 600,
    color: "white",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  benefitsList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  benefitItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
    padding: "8px 0",
  },
  benefitIcon: {
    color: "#4CAF50",
    fontSize: "16px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
  },
  benefitText: {
    color: "#cccccc",
    fontSize: "14px",
    lineHeight: 1.4,
  },
  dialogActions: {
    padding: "16px 24px",
    borderTop: "1px solid #333",
    justifyContent: "center",
  },
  closeBtn: {
    background: "#FE5A97",
    color: "white",
    padding: "12px 32px",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 600,
    "&:hover": {
      background: "#ff85c1",
    },
  },
  description: {
    color: "#cccccc",
    fontSize: "16px",
    lineHeight: 1.6,
    textAlign: "center",
    marginBottom: "20px",
  },
}));

const benefits = {
  freemium: [
    "Free access",
    "Buy credits separately",
    "Basic platform features",
    "Limited search",
  ],
  onyx: [
    "Onyx badge in profile",
    "Priority DM delivery",
    "60 credits per month",
    "Basic platform features",
  ],
  sapphire: [
    "Sapphire badge in profile",
    "Priority DM delivery",
    "Advanced search",
    "1 daily DM boost",
    "180 credits per month",
    "Priority support",
  ],
  diamond: [
    "Diamond badge in profile",
    "Priority DM delivery",
    "Advanced search",
    "3 daily DM boosts",
    "Profile highlighting",
    "400 credits per month",
    "Exclusive features",
  ],
  platinum_vip: [
    "Platinum badge in profile",
    "Priority DM delivery",
    "Advanced search",
    "5 daily DM boosts",
    "Profile highlighting",
    "Personal manager",
    "1200 credits per month",
    "VIP support 24/7",
    "Exclusive content",
  ],
};

const getTierIcon = (tier: string) => {
  switch (tier) {
    case "freemium":
      return <Gift size={20} color="#28a745" />;
    case "onyx":
      return <Star size={20} color="#6c757d" />;
    case "sapphire":
      return <Zap size={20} color="#17a2b8" />;
    case "diamond":
      return <Gem size={20} color="#ffc107" />;
    case "platinum_vip":
      return <Crown size={20} color="#667eea" />;
    default:
      return <Gift size={20} color="#28a745" />;
  }
};

const getTierClass = (tier: string): string => {
  switch (tier) {
    case "freemium":
      return "tierFreemium";
    case "onyx":
      return "tierOnyx";
    case "sapphire":
      return "tierSapphire";
    case "diamond":
      return "tierDiamond";
    case "platinum_vip":
      return "tierPlatinum";
    default:
      return "tierFreemium";
  }
};

const SubscriptionBenefits: React.FC<SubscriptionBenefitsProps> = ({
  isOpen,
  onClose,
}) => {
  const classes = useStyles() as any;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h4" style={{ color: "#FE5A97", fontWeight: 600 }}>
          Subscription Benefits
        </Typography>
        <Button onClick={onClose} className={classes.closeButton}>
          <X size={24} />
        </Button>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.description}>
          Choose the plan that best fits your needs. Each plan offers unique
          benefits and features.
        </Typography>

        <Grid container spacing={3}>
          {Object.entries(benefits).map(([tier, tierBenefits]) => (
            <Grid item xs={12} sm={6} key={tier}>
              <Box
                className={`${classes.benefitsTier} ${
                  classes[getTierClass(tier)]
                }`}
              >
                <Box className={classes.tierHeader}>
                  <Box className={classes.tierIcon}>{getTierIcon(tier)}</Box>
                  <Typography className={classes.tierName}>
                    {tier.toUpperCase()}
                  </Typography>
                </Box>

                <ul className={classes.benefitsList}>
                  {tierBenefits.map((benefit, index) => (
                    <li key={index} className={classes.benefitItem}>
                      <Check className={classes.benefitIcon} />
                      <span className={classes.benefitText}>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button className={classes.closeBtn} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionBenefits;
